import React, { useEffect } from "react";
import "./Changelog.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faScroll } from "@fortawesome/free-solid-svg-icons";

function ChangelogEntry({ date, title, items }) {
  const renderItem = (item) => {
    if (typeof item === 'string') {
      return item;
    }
    // If item is an object with text and subitems
    if (item.text && item.subitems) {
      return (
        <>
          {item.text}
          <ul className="changelog-sublist">
            {item.subitems.map((subitem, j) => (
              <li key={j}>{subitem}</li>
            ))}
          </ul>
        </>
      );
    }
    return item;
  };

  return (
    <div className="changelog-entry">
      <div className="changelog-date">{date}</div>
      <h3 className="changelog-title">{title}</h3>
      <ul className="changelog-list">
        {items.map((it, i) => (
          <li key={i}>{renderItem(it)}</li>
        ))}
      </ul>
    </div>
  );
}

export default function Changelog() {
  // Example entries — add new entries as needed
  const entries = [
    {
      date: "2026-10-24",
      title: "DFS lineups lock once a player has kicked off",
      items: [
        "A submitted lineup is meant to be frozen as soon as one of its players starts their game. That check had not worked for months \u2014 a key format changed when DFS gained the Thursday and Monday slates and the check was never updated, so it quietly passed every lineup. It works again.",
        "Defences now count towards that lock. They score like any other slot but were being skipped, so a defence that had already played could be swapped for one that had not. A lineup whose defence has played will now lock where before it did not.",
        "Organisers can choose, per tournament, between freezing the whole lineup once anybody has played and freezing only the players who have played. The whole lineup stays the default, so nothing changes for a tournament already running unless you change it.",
        "The DFS page shows a league as locked, naming the player who has already played, instead of letting you build a replacement lineup and refusing it when you press submit. Where only some players are frozen it says which ones to keep.",
        "The manage page keeps each entrant's last ten submissions for the week in progress, so a lineup that was replaced can still be looked at. They are discarded once the week is scored.",
        "Lineups are now actually kept secret until a tournament's reveal time. The reveal time had been recorded but never enforced, so anyone who knew a tournament's name could read every entry at any point \u2014 including before choosing their own. Who has submitted, and when, stays visible; what they picked does not. Organisers signed in with their Sleeper account still see everything.",
      ],
    },
    {
      date: "2026-10-24",
      title: "DFS lineups can be resubmitted after an edit",
      items: [
        "Submitting a lineup to a league left that league's button reading \"Lineup submitted\" even after you changed the lineup, so there was no way to send the new one without switching pages or refreshing first. Changing the lineup now clears those markers, and the leagues can be submitted to again.",
      ],
    },
    {
      date: "2026-10-22",
      title: "Gameday's season table remembers finished weeks",
      items: [
        "The season view kept its figures only for as long as the browser tab stayed open, so coming back a day or two later meant loading every league again. Finished weeks are now kept properly and are there when you return.",
        "Only the four numbers each row shows are kept, not the raw scores behind them — about thirty kilobytes for a whole season rather than four megabytes, which is what makes keeping them possible at all.",
      ],
    },
    {
      date: "2026-10-22",
      title: "Find a player lights up the right player",
      items: [
        "Searching a surname on the League page could light up leagues that did not hold the player it named. Searching \"etienne\" lit four leagues and named Travis Etienne, who was in one of them — the other three held Trevor Etienne. The leagues lit are now always the ones holding the player shown.",
        "A search no longer matches the middle of a word, so \"dell\" finds Tank Dell rather than Odell Beckham, and a single word is treated as a surname, so \"chase\" finds Ja'Marr Chase rather than Chase Brown.",
        "When a query still describes more than one player, the page says which one it picked and how many others matched, instead of quietly choosing.",
      ],
    },
    {
      date: "2026-10-22",
      title: "Anytime TD props retired",
      items: [
        "Anytime TD is no longer fetched, shown or graded. It cost the same per game as any other prop market and was the least trustworthy of them: its projection is a scoring rate guessed from a handful of games, and no bookmaker offers the other side of it, so a good share of its flagged calls were never bets you could have placed.",
        "The Odds page no longer offers an Anytime TD tab, and the results page leaves it out of the hit rates so the numbers stay comparable week to week.",
      ],
    },
    {
      date: "2026-10-20",
      title: "Trade Helper compares pick holdings side by side",
      items: [
        "The two draft pick columns in League Info were both the other manager's, one per season. They are now yours and theirs, so you can see at a glance who is actually richer in picks.",
        "Which season they show is chosen by a pair of connected pills under the heading, so you can flip between next year and the year after without losing the comparison.",
      ],
    },
    {
      date: "2026-10-16",
      title: "Trade Tracker",
      items: [
        "A new page under Trade Helper listing every trade still waiting on a decision, across all your leagues at once, grouped by league and badged as awaiting you or waiting on them.",
        "Each offer shows the same detail the Trade Helper does — KeepTradeCut and FantasyCalc values, age, projected and actual points per game — with totals for both sides and the gap between them.",
        "Draft picks are placed using each manager's max points for: the lowest ceiling drafts first, with projected playoff teams pushed to the back. That gives every pick an Early/Mid/Late band and a projected slot like 1.04, which is what prices it.",
        "Where a pick cannot be placed — nobody has scored yet, or the roster has left the league — it says so rather than guessing a value.",
      ],
    },
    {
      date: "2026-10-15",
      title: "Start/Sit bench tidy-up",
      items: [
        "The bench control now sits between the starters and the bench, so the bench opens below it instead of pushing it off the bottom — closing a long bench no longer means scrolling to find the button again.",
        "Bench players show their own position only. In a SuperFlex league every one of them carried an SFLX beside it, which was true of all of them and so told you nothing.",
        "The SuperFlex icon leads with the quarterback, the band a plain flex does not have, so the two read as the same icon with one added.",
      ],
    },
    {
      date: "2026-10-14",
      title: "Gameday shows what a lineup could have scored",
      items: [
        "A Season view lists every league side by side, with each week\u2019s result, points, the best that roster could have started, and how much of it you actually got \u2014 averaged at the foot of each table, with your record for the weeks loaded.",
        "The percentage column is shaded: around three quarters reads neutral, and it moves towards red or green from there. A week where you started the best lineup available is gold.",
        "Weeks load when you ask for them. Each unloaded week says so and fetches on click, or Load all fetches the lot. Nothing is pulled down before you want it.",
        "You can move between weeks from a bar across the top, and a finished week is only ever fetched once.",
        "Once every player in a matchup has finished, each score carries a quiet second line showing the best that roster could have started \u2014 yours and your opponent\u2019s.",
        "Hovering it says how many points were left on the bench.",
        "It only appears when the matchup is settled. Part way through, a best lineup only means the best of what has been played so far, and it would keep climbing all weekend.",
      ],
    },
    {
      date: "2026-10-14",
      title: "Gameday keeps the finished week through Tuesday",
      items: [
        "Sleeper moves to the next week as soon as Monday night football ends, so from Tuesday morning Gameday was showing an empty upcoming fixture and the result you watched the night before had gone.",
        "The finished week now stays up through Tuesday, and the new one arrives on Wednesday.",
        "A Monday night game still being played is never hidden, even though it falls on a Tuesday morning here.",
        "A tab left open across the rollover picks the new week up on its own, where before it sat on the old one until the page was reloaded.",
        "The heading says which week is being shown, since on a Tuesday that is no longer the one you would assume.",
        "On the Leagues page, the Empty bench and Injuries headings sit on two lines. Their length was setting the width of two narrow columns and pushing the whole table into sideways scrolling on smaller screens.",
      ],
    },
    {
      date: "2026-10-12",
      title: "Gameday: finished matchups stand out",
      items: [
        "A matchup whose players have all finished gets a green or red edge, so a settled result is visible without reading the numbers.",
        "It waits for both sides. A matchup where your own players are done but the opponent still has a Monday night game is not a result yet, however far ahead you are.",
        "A drawn matchup gets a neutral edge rather than being called a loss, and reads T instead of L.",
      ],
    },
    {
      date: "2026-10-11",
      title: "DFS: clearer picking",
      items: [
        "Typing a name to find a player now clears the search as soon as that player is added, so the next search starts from an empty box instead of needing the old one deleted first.",
        "It only clears when the player actually went in \u2014 if there was no slot left or no room under the cap, what you typed stays put.",
        "My Team shows when each player kicks off, and when the first of them does. That first kickoff is when the lineup starts locking, so it sits beside the heading where it is hard to miss.",
        "Those times are in your own timezone rather than the American one the salary data quotes. A Sunday night game reads as Monday morning here, which is when it actually is.",
      ],
    },
    {
      date: "2026-10-11",
      title: "Players stuck on injured reserve",
      items: [
        "A second card above the league table points out the opposite problem: somebody sitting on injured reserve who has recovered and no longer qualifies for the slot.",
        "This one is reported even when it cannot be fixed. Moving a player back needs a free bench spot, and if there is none the card still says so \u2014 the roster is wrong either way, and a league that needs a drop is worth knowing about.",
        "In that case the players are listed but greyed out, with a line saying why.",
        "Which players count as no longer qualifying follows the same league rules as the other direction, so a league that allows Out players on reserve will not flag them.",
        "Both cards list the first few leagues and offer to show the rest. With enough teams this can apply in dozens of leagues at once, and a card naming all of them was unreadable.",
      ],
    },
    {
      date: "2026-10-10",
      title: "Position colours, pills, and a rebuilt league table",
      items: [
        "Every page now agrees on what colour a position is. The draft board, DFS, the tournament results, the betting pages and the stats tables each had their own idea \u2014 a wide receiver was blue in one place and yellow in another, and a tight end orange or purple depending on where you looked.",
        "Kickers had no colour at all on the DFS page and were drawn in the running backs' green on the results page. They have their own colour now, and defences are coloured everywhere rather than only in some places.",
        "Start/Sit shows each slot as a coloured pill instead of plain text. A flex slot is split into a band per position it accepts, so FLEX reads across wide receiver, running back and tight end, and a superflex adds quarterback \u2014 you can see what fits a slot without reading the word.",
        "The bench list shows the same pills, listing a player\u2019s own position and the widest flex they fit rather than every flex separately.",
        "The DFS roster shows a flex the same way \u2014 the bands replace the flat purple, which said nothing about what the slot takes.",
        "The Leagues page now uses the same table as Start/Sit, so the two read alike instead of each having their own spacing, headers and borders.",
        "Positions in a league\u2019s roster and in the Portfolio tab are shown as pills too.",
        "A new Empty bench column shows how many roster spots are still free. Players on injured reserve or the taxi squad do not take one up, so the count is what you can actually still add.",
        "Standings position and max points sit beside the record now, where they are easier to compare, and both are centred under their headings.",
        "Highlighting a league, or picking a player out of a search, no longer nudges the rest of the row sideways.",
        "Above the league table, anything worth acting on now shows as a card. The first is a player sitting on your bench who could be on injured reserve while that roster still has a reserve slot free \u2014 one card covers every league it applies to.",
        "Fix opens a list of everyone who qualifies, grouped by league and ticked as far as each league\u2019s own free slots allow. Where more players qualify than there is room for, the rest are closed off until you untick one, so you cannot pick more than will fit.",
        "Continue clears every league at once. If one of them refuses, the others still go through and the dialog stays open saying which one did not.",
        "A Clear all button empties the selection in every league, so you can start from nothing and pick just the one you meant.",
        "Which players qualify follows each league\u2019s own reserve rules, so a league that does not allow Out players on reserve will not offer them.",
        "Position colours follow the light and dark themes properly now. Several pages had them fixed to one set of colours regardless of the theme.",
      ],
    },
    {
      date: "2026-10-06",
      title: "Better Start/Sit suggestions",
      items: [
        "A slot that keeps its own starter no longer holds a bench player back from a slot that needs one. Some lineups read \u201cno eligible replacement\u201d while a perfectly good substitute sat on the bench, reserved by a slot that was never going to use them.",
        "Slots whose starter cannot play are now served first, so the best available bench player goes where it matters most instead of being taken by a slot that was only slightly behind.",
        "Where a starter is on bye or ruled out, backups Sleeper does not publish a projection for can now be suggested and picked. A player who might score something is better than one who certainly will not, and the slot can be changed rather than being stuck.",
        "Those players show a dash instead of a projection, and are never suggested ahead of someone who is actually playing.",
        "Bye weeks are now read from the schedule, so a player whose team is idle is always marked as on bye \u2014 previously an injury designation could hide it and the row said the projection was simply missing.",
        "When every bench player who fits a slot is already needed by another, the row says so rather than claiming there is nobody eligible.",
        "Taxi squad and injured reserve players are no longer offered as substitutions after a lineup change has been made on the page. They were correctly left out when the page first loaded, but came back once a change had gone through.",
      ],
    },
    {
      date: "2026-09-20",
      title: "Show or hide leagues",
      items: [
        "Settings has a new Show / hide leagues section listing every dynasty and redraft league with a tick box each.",
        "Unticked leagues are left out of Leagues, Gameday and Start/Sit, so a page only shows the leagues you actually follow.",
        "The choice is remembered in this browser and saves as you tick, with Show all and Hide all for the whole list at once.",
        "Best ball is deliberately absent: those leagues have no lineup to set. A league joined later shows up by default rather than staying hidden.",
        "Start/Sit can also be pointed at the name in the menu instead of the account you are signed in as, so another manager's lineups can be looked at without signing out.",
      ],
    },
    {
      date: "2026-09-19",
      title: "Start/Sit",
      items: [
        "A new Start/Sit page reads every head-to-head league at once and flags the lineup slots worth a second look.",
        "Projections are Sleeper's own, priced with each league's scoring — so TE premium and SuperFlex are counted properly rather than shown as plain PPR.",
        "Each slot is compared against the bench players eligible for it; a gap of a point or more is flagged, and one player is only ever suggested for one slot.",
        "Players on bye, ruled out, or missing a projection are flagged whatever the numbers say, and slots whose game has kicked off are shown as locked.",
        "Each row carries the evidence behind the call: matchup difficulty, recent form and injury status, with the bench listed underneath showing which slots each player can fill.",
        "Dynasty leagues sort above redraft ones under their own headers, and a filter narrows the page to just the leagues needing attention.",
        "If the stats backend cannot be reached the page says which columns are affected, rather than leaving them blank with no explanation.",
      ],
    },
    {
      date: "2026-09-16",
      title: "Waivers by week",
      items: [
        "The league page's Waivers tab now shows only the current week's claims, counted from Tuesday's week rollover.",
        "Claims still waiting to process stay in the list however long ago they were entered.",
        "Earlier weeks moved into an Archive you can expand.",
      ],
    },
    {
      date: "2026-09-12",
      title: "Live Gameday Projections",
      items: [
        {
          text: "Gameday now shows a live projection instead of the pre-kickoff one:",
          subitems: [
            "Starters who have played count for what they actually scored; everyone else still counts for their projection",
            "This matches the number Sleeper's own app shows, including in leagues with TEP or other custom scoring",
            "The pre-kickoff projection is kept underneath, so you can see which way the week is going",
            "Each side is counted separately — a team with nobody played still shows its plain projection",
            "Win/loss colours and the weekly W/L summary now follow the live number",
          ],
        },
        "Each matchup shows how many of your starters have played, as 3/10, alongside the same for your opponent.",
        {
          text: "New Auto-refresh toggle, off by default:",
          subitems: [
            "Refreshes every minute while games are being played",
            "Pauses whenever the tab is in the background, and catches up the moment you return to it",
            "Stays idle when no game is in progress, so it costs nothing outside gameday",
          ],
        },
      ],
    },
    {
      date: "2026-08-25",
      title: "More Prop Markets & Prediction Tracking",
      items: [
        {
          text: "Two new player prop markets:",
          subitems: [
            "Receptions — how many catches a player is expected to make",
            "Rush Attempts — how many carries a player is expected to get",
          ],
        },
        "Market tabs are now ordered so the volume stat comes before the yardage it produces: Pass Yds, Rush Att, Rush Yds, Receptions, Rec Yds, Anytime TD.",
        {
          text: "New Prop Results tab, which tracks how well the predictions actually do:",
          subitems: [
            "Captures the betting line before each game, then compares it against what the player actually did",
            "Shows how often the flagged value picks were right, both overall and per market",
            "Tracks how far a line moved after it was first seen",
            "Hit rates are greyed out until enough games have been graded to mean anything",
          ],
        },
        {
          text: "Anytime Touchdown props now read properly:",
          subitems: [
            "Shown as a percentage chance to score rather than a touchdown count",
            "Last 5 shows a simple scored / didn't score marker for each game",
            "Compared against the odds on offer, so a player only stands out when recent scoring beats what the price suggests",
          ],
        },
        "New Proj column on Player Props showing the projection the value percentage is based on — previously only the raw recent average was visible, which made the value figure look wrong.",
        "Player projections now account for how tough or easy the opposing defence has been against that position.",
        "On the Leagues page, the waiver P/W/L labels are dimmed and zero counts faded, so the numbers that matter stand out.",
      ],
    },
    {
      date: "2026-08-11",
      title: "Betting Page Upgrades",
      items: [
        "Renamed the Odds page to Betting.",
        {
          text: "Player Props got a big upgrade:",
          subitems: [
            "Search for a player by name",
            "Filter to a single team using the new Teams button",
            "New Last 5 column showing a player's last 5 games for that stat, colored against the current line",
            "New Opp Def column showing how tough or easy the opponent's defense is for that position and stat, color-coded from red (tough matchup) to green (easy matchup)",
            "New Date column showing kickoff time",
            "Simplified pricing — for Anytime Touchdown props, the price now shows directly in the Line column instead of separate Over/Under columns",
            "Player props now become available earlier in the offseason instead of waiting until two weeks before kickoff",
          ],
        },
        "Defense rank colors (Stats and Betting pages) now use a smooth red-to-green gradient instead of flat colors, making it easier to spot the toughest and easiest matchups at a glance.",
        "All game and kickoff times now display in 24-hour format.",
        "Player teams and matchups now stay accurate through offseason trades.",
      ],
    },
    {
      date: "2026-07-01",
      title: "Odds Results Tab",
      items: [
        {
          text: "New Results tab on the Odds page:",
          subitems: [
            "Shows historical games with the betting lines that were available before kickoff",
            "Displays the final score, moneyline winner, and the actual over/under total",
            "Highlights whether our implied-total edge prediction was correct (green) or incorrect (red)",
            "Push outcomes are shown neutrally",
          ],
        },
      ],
    },
    {
      date: "2026-06-28",
      title: "Gameday Page & Improved DvP",
      items: [
        {
          text: "New Gameday page (football icon in the menu):",
          subitems: [
            "Shows your weekly matchup projections across all your dynasty and redraft leagues",
            "Summary header shows your predicted record for the week (e.g. 7W / 3L)",
            "Your projected score is always on the left, highlighted green if predicted to win or red if predicted to lose",
            "Leagues are grouped into Dynasty and Redraft sections",
            "Toggle between a card view and a list view",
            "Requires logging in via Settings with your Sleeper account",
          ],
        },
        {
          text: "Defense vs Position (DvP) on the Leagues page now uses stats-based data:",
          subitems: [
            "Ranks are now position-specific (separate rankings for QB, RB, WR, TE) based on fantasy points allowed this season",
            "Hover over any DvP value to see the last-5-game rolling rank",
          ],
        },
      ],
    },
    {
      date: "2026-06-28",
      title: "Odds Page",
      items: [
        {
          text: "New Odds page for tracking weekly betting opportunities:",
          subitems: [
            "Game Lines tab: view spread, total, and moneyline for each matchup, with an edge indicator comparing the book total to our own implied total",
            "Player Props tab: browse player prop lines (passing yards, rushing yards, receiving yards, anytime TD) alongside the best available over/under price across books",
            "Props include a 5-game rolling average per stat, with a value flag highlighting lines that deviate meaningfully from recent performance",
            "Filter props by position (QB/RB/WR/TE) and market type, or show value-flagged props only",
          ],
        },
        "Betting rules and recommendations are not yet active (off-season) but will be introduced when the season begins",
      ],
    },
    {
      date: "2026-05-25",
      title: "Stats Page & Sleeper Account Login",
      items: [
        {
          text: "New Stats page:",
          subitems: [
            "Browse live NFL player stats for the current season, filterable by position",
            "Receiver efficiency metrics (Target Share, Air Yards Share, WOPR, RACR) shown for WR, TE, and RB",
            "Toggle between season totals and per-game averages",
            "Team view with offensive efficiency across the league, with color-coded team badges",
            "Click a team to drill down and see all players on that roster",
          ],
        },
        {
          text: "Optional Sleeper account login (Settings):",
          subitems: [
            "Connect your Sleeper account with username and password or SMS verification",
            "When logged in as the active username, pending waiver claims appear in the Leagues page alongside completed and failed transactions",
            "Cancelled waiver claims are filtered out and not shown",
            "The pending count (P:) in the league summary is only visible when logged in",
          ],
        },
        "Waiver rows now show the date on the left for easier scanning",
      ],
    },
    {
      date: "2026-03-28",
      title: "Draft Helper Grid View",
      items: [
        {
          text: "New grid board view in Draft Helper:",
          subitems: [
            "Toggle between the standard position list view and a 12-wide draft board showing all picks round by round",
            "Each row shows one complete round — the same layout you'd see at a live draft table",
            "Drafted players appear greyed out on the board instead of disappearing, so you can see which slots are filled",
            "Click a greyed-out player to undraft them",
          ],
        },
        "Grid view supports both Snake and 3RR (Third Round Reversal) draft formats, with a toggle to switch between them",
        "3RR pick order correctly follows the format: round 1 forward, round 2 reversed, round 3 stays reversed, then snakes normally from round 4",
      ],
    },
    {
      date: "2026-02-02",
      title: "Create Rankings from Drafts, League Filters & Year Update",
      items: [
        {
          text: "Create Rankings from Drafts (Create Rankings Setup):",
          subitems: [
            "New section: enter one or more draft IDs (comma-separated), Add Drafts, then Compile Rankings to build rankings from average ADP across drafts",
            "Draft names from Sleeper draft metadata (no separate league API call)",
            "'My Leagues' dropdown: fetch your leagues for the current year (from Sleeper state endpoint, fallback 2026), then add a draft from the dropdown",
            "When compiling: position tiers use 5 players per tier; overall list tiers unchanged (12 per tier)",
          ]
        },
        "Draft Modal: draft ID and copy-to-clipboard icon shown next to the league title",
        {
          text: "League list:",
          subitems: [
            "'Dynasty only' checkbox (default on) filters leagues by Sleeper settings.type === 2",
          ]
        },
        {
          text: "Trade Helper:",
          subitems: [
            "Panel titles swapped: left box titled 'User gets', right box 'Trade Partner gets'; logic and league-info column unchanged",
          ]
        },
        "Year 2025 → 2026 across Trade Analyzer, Drafts List, League List, Bestball, and Create Rankings Setup (DEFAULT_LEAGUE_YEAR constant)",
        "Draft/Rankings Setup: preset options no longer show a selected state; clicking a preset navigates immediately",
        {
          text: "UI updates:",
          subitems: [
            "Create Rankings: player buttons use a single glassmorphism style (gradient, backdrop blur, colored border) for all positions at medium opacity",
            "League list: injured-player indicator is custom cross.png (medical cross) at 1.1rem, aligned with surrounding text",
            "Setup pages: preset choices no longer display a highlighted/selected style",
          ]
        },
      ],
    },
    {
      date: "2025-12-18",
      title: "Bestball & Tournament Enhancements",
      items: [
        {
          text: "Bestball Overview:",
          subitems: [
            "Added 'Behind 1st' column showing points difference from first place",
            "Shows positive value (+X) when in first place (points ahead of 2nd), negative value (X) when not in first (points behind 1st)",
            "Leagues sorted within each position by 'Behind 1st' value (closest to 1st first, except position 1 sorted by furthest ahead first)"
          ]
        },
        {
          text: "Tournament Creation:",
          subitems: [
            "Added league source selection: choose between manually entering Sleeper League IDs or selecting from 'My Own Leagues'",
            "'My Own Leagues' option fetches and displays your bestball leagues from Sleeper with a two-box selector interface",
            "Participant count now uses an input field allowing any number, with a quick-select dropdown for common values (2, 4, 8, 16, 32, 64)",
            "H2H mode automatically enforces even numbers only and adjusts participant count when switching from PTS mode"
          ]
        }
      ],
    },
    {
      date: "2025-12-15",
      title: "Tournament Feature",
      items: [
        {
          text: "Tournament Management:",
          subitems: [
            "View list of all tournaments with name, week, and participant count",
            "Create new tournaments with custom matchups between players from different Sleeper leagues",
            "Two-step creation process: first paste league IDs to load league data, then select matchups",
            "Select tournament week (current or next week)",
            "Choose number of participants in powers of 2 (2, 4, 8, 16, 32, or 64)",
            "Players sorted by total points with rankings (1#, 2#, etc.) in dropdown selections"
          ]
        },
        {
          text: "Tournament Results:",
          subitems: [
            "View tournament results with matchup-by-matchup point comparisons",
            "See league names, player rankings, usernames, and points for each matchup",
            "Results calculated using optimal lineup based on league roster settings and Sleeper matchup data"
          ]
        }
      ],
    },
    {
      date: "2025-12-09",
      title: "DFS PIN Codes & Reveal Updates",
      items: [
        {
          text: "PIN-protected lineups",
          subitems: [
            "Optional PIN (2-8 digits) when saving a lineup to tinyurl; validation enforces digits-only and minimum length when loading",
            "Load flow shows PIN-gated lineups in green; prompts for PIN and calls the data endpoint with username + pin",
            "Finish-lineup modal PIN input allows empty or 2-8 digits; load PIN modal requires 2+ digits"
          ]
        },
        {
          text: "Reveal & pre-game masking",
          subitems: [
            "Players whose games have not started are masked as TBD (name/salary/points hidden) with the same styling as pre-reveal",
            "Fallback TBD guard ensures players stay hidden until kickoff even if partial data arrives",
          ]
        }
      ],
    },
    {
      date: "2025-11-13",
      title: "DFS League Management & Results Improvements",
      items: [
        {
          text: "DFS Results Page:",
          subitems: [
            "Added 'Your Leagues' section at the top showing all leagues you're a member of with quick navigation links",
            "Fixed results display to show all user submissions from leagues, not just one entry",
            "Added submission status indicator in Total Points column (green for Submitted, red for Not submitted) when reveal time hasn't passed",
          ]
        },
        {
          text: "DFS Lineup Submission:",
          subitems: [
            "Added ability to submit your DFS lineup to leagues you're a member of directly from the lineup modal",
            "View available leagues with submission status (green for new submission, yellow for overwrite existing)",
            "Confirmation modal when overwriting existing lineup data",
            "Load previously submitted lineups from your leagues in the 'Load Lineup' modal",
          ]
        },
        {
          text: "TinyURL Creation:",
          subitems: [
            "Support for creating leagues with mix of users who have submitted and users who haven't",
            "Default reveal time set to upcoming Sunday at 19:00 with clear button to remove",
          ]
        }
      ],
    },
    {
      date: "2025-11-06",
      title: "DFS Day Filter",
      items: [
        "Added day filter buttons to DFS page - filter players by game day (Thursday, Sunday, Monday, etc.)",
      ],
    },
    {
      date: "2025-10-25",
      title: "DFS Lineup Loading & Injury Filters",
      items: [
        "Added 'Load Lineup' button to DFS page - paste lineup codes to instantly load players",
        "Support for both formats: 'Username:Code' and raw lineup codes",
        "Added injury status filters: 'Hide Out' and 'Hide Questionable' toggles",
      ],
    },
    {
      date: "2025-10-21",
      title: "DFS & League Page Enhancements",
      items: [
        {
          text: "DFS (Daily Fantasy Sports) - New Feature:",
          subitems: [
            "Build your DFS lineup with salary cap management ($50,000 cap)",
            "Live player data with weekly projections, salaries, and matchup info",
            "Advanced filters: salary range slider, team dropdown, position buttons, and player name search",
            "Visual roster builder with color-coded position badges",
            "Smart availability tracking - automatically disable players when positions are full or over salary cap",
            "Generate shareable lineup codes for competition tracking",
            "Results page to compare multiple lineups side-by-side with actual fantasy points",
            "Automatic ranking and podium medals (gold/silver/bronze) for top performers",
            "Week toggle to view results for current or previous week",
          ]
        },
        {
          text: "League Page Improvements:",
          subitems: [
            "Added FPTS/G (Fantasy Points Per Game) column showing season average",
            "Added Proj Pts column with this week's projected fantasy points",
            "Added DvP (Defense vs Position) column showing opponent defensive rank with ordinal suffixes",
            "Color-coded matchups: red for favorable (1st-10th), green for tough (22nd-32nd)",
            "Reordered columns to prioritize weekly performance data over dynasty values",
          ]
        },
      ],
    },
    {
      date: "2025-10-07",
      title: "Trade Helper",
      items: [
        "Search and add players or draft picks to both sides of a trade",
        "Compare dynasty values using KTC and FantasyCalc rankings",
        "Analyze projected vs actual 2025 points per game",
        "View average team age and see which side wins each metric",
        "Automatic league info display showing which teams own the players",
      ],
    },
    {
      date: "2025-09-01",
      title: "First changelog and Stats & Bestball improvements",
      items: [
        "Added Draft Position column to Bestball table",
        "Added owner names in Bestball table",
        "Added Stats tab with Head-to-Head checker and other stats",
        "Added a owners box to the leage page to check what leagues the owners are in",
        "Fuzzy search for players on league page",
      ],
    },
  ];

  return (
    <div className="changelog-page">
      <div className="changelog-header">
        <FontAwesomeIcon icon={faScroll} className="changelog-icon" />
        <div>
          <h2>Changelog</h2>
        </div>
      </div>

      <div className="changelog-entries">
        {entries.map((e, idx) => (
          <ChangelogEntry
            key={idx}
            date={e.date}
            title={e.title}
            items={e.items}
          />
        ))}
      </div>
    </div>
  );
}

// When the user opens the changelog page we should mark it as read
// Use a side-effect so mount writes last_read_changelog
export function ChangelogWithReadMark() {
  useEffect(() => {
    try {
      localStorage.setItem("last_read_changelog", new Date().toISOString());
      // Dispatch a storage event for same-tab listeners
      window.dispatchEvent(new Event("storage"));
    } catch (e) {}
  }, []);
  return <Changelog />;
}
